import { CharacterEmotionCategory, CharacterEmotionCue } from '../types/character';

export interface FormattedParagraph {
  type: 'narrative' | 'dialogue' | 'marginalia' | 'lore';
  text: string;
  speaker?: string;
  speakerRole?: string;
  characterId?: string;
  accentColor?: string;
  emotion?: string;
  emotionCategory?: CharacterEmotionCategory;
  emotionColor?: string;
  stageDirection?: string;
}

/**
 * Maps emotion tags and dialogue sentiment to discrete vocal & visual biometric categories.
 */
export function detectEmotionFromText(
  text: string,
  speakerName?: string,
  explicitTag?: string
): CharacterEmotionCue {
  if (explicitTag) {
    const lower = explicitTag.toLowerCase();
    if (/terror|panic|hyperventilat|trembl|quiver|spasm|fear|scream|agony|anguish|plea|shock|weep/i.test(lower)) {
      return { tag: explicitTag.toUpperCase(), category: 'terror', color: '#ff1744', intensity: 0.95 };
    }
    if (/dread|weary|exhaust|fatigue|decay|strain|hollow|heavy|patience|memory/i.test(lower)) {
      return { tag: explicitTag.toUpperCase(), category: 'dread', color: '#ff9100', intensity: 0.85 };
    }
    if (/contempt|inquest|rage|anger|threat|authorit|defiance/i.test(lower)) {
      return { tag: explicitTag.toUpperCase(), category: 'contempt', color: '#ff3d00', intensity: 0.90 };
    }
    if (/void|margin|spectator|non-euclidean|timeless|transcend|whisper/i.test(lower)) {
      return { tag: explicitTag.toUpperCase(), category: 'void', color: '#d500f9', intensity: 0.90 };
    }
    if (/clinic|psych|diagnos|evaluat|aphasia/i.test(lower)) {
      return { tag: explicitTag.toUpperCase(), category: 'clinical', color: '#b388ff', intensity: 0.75 };
    }
    if (/calculat|precis|vector|resolv|command|stoic/i.test(lower)) {
      return { tag: explicitTag.toUpperCase(), category: 'calculation', color: '#00e5ff', intensity: 0.80 };
    }
    return { tag: explicitTag.toUpperCase(), category: 'alarm', color: '#00e676', intensity: 0.70 };
  }

  // Infer from dialogue content and speaker persona
  const t = text.toLowerCase();
  const s = (speakerName || '').toLowerCase();

  if (s.includes('silas') || /hyperventilat|clock broken|hands shaking|is that what happened|panic|god/i.test(t)) {
    return { tag: 'TREMBLING PANIC', category: 'terror', color: '#ff1744', intensity: 0.92 };
  }
  if (s.includes('thorne') || /inquest|penal|tribunal|deviat|arrest|conspiracy/i.test(t)) {
    return { tag: 'AUTHORITARIAN CONTEMPT', category: 'contempt', color: '#ff3d00', intensity: 0.88 };
  }
  if (s.includes('margin') || s.includes('spectator') || /we watch|unthread|omitted|cosmic ledger/i.test(t)) {
    return { tag: 'NON-EUCLIDEAN HOLLOWNESS', category: 'void', color: '#d500f9', intensity: 0.94 };
  }
  if (s.includes('peter') || s.includes('simon') || /retina|optic scan|aphasia|eeg|synapse/i.test(t)) {
    return { tag: 'CLINICAL DETACHMENT', category: 'clinical', color: '#b388ff', intensity: 0.78 };
  }
  if (s.includes('elena') || /coordinates|vector|bypass|resonance|ninety seconds|induction/i.test(t)) {
    return { tag: 'COLD PRECISION', category: 'calculation', color: '#00e5ff', intensity: 0.82 };
  }
  if (s.includes('vox') || /telemetry|warning|alert|kilovolts/i.test(t)) {
    return { tag: 'SYNTHETIC AUTOPILOT', category: 'alarm', color: '#00e676', intensity: 0.65 };
  }

  return { tag: 'CHRONOGNOSTIC DREAD', category: 'dread', color: '#ff9100', intensity: 0.84 };
}

/**
 * Strips mechanical markers like "ACT I: ... [PAGE 11 OF 1000] —"
 * so the story flows like a published literary novel.
 */
export function cleanNarrativeText(rawText: string): string {
  let cleaned = rawText
    .replace(/^ACT\s+[IVXLCDM]+:\s+[^/]+\/\/\s+CHAPTER\s+\d+:\s+[^\[]+\[PAGE\s+\d+\s+OF\s+\d+\]\s*[—–-]\s*/i, '')
    .replace(/^\[PAGE\s+\d+\s+OF\s+\d+\]\s*[—–-]\s*/i, '')
    .trim();

  return cleaned;
}

/**
 * Formats paragraphs into literary prose blocks with detected dialogue & marginalia.
 */
export function formatPageParagraphs(
  rawParagraphs: string[],
  _pageSpeaker?: string
): FormattedParagraph[] {
  return rawParagraphs.map((para) => {
    const cleaned = cleanNarrativeText(para);

    // 1. Detect marginalia, cosmic ledger, or diagnostic telemetry notes
    if (
      /^(COSMIC LEDGER NOTE|DIAGNOSTIC TELEMETRY|TRANSIT RECORD|CHRONO-LOG|SYSTEM WARNING|TELEMETRY LOG)/i.test(
        cleaned
      )
    ) {
      const colonIdx = cleaned.indexOf(':');
      if (colonIdx > 0 && colonIdx < 50) {
        const label = cleaned.slice(0, colonIdx).trim();
        const body = cleaned.slice(colonIdx + 1).trim();
        return {
          type: 'marginalia',
          text: body || cleaned,
          speaker: label,
          accentColor: '#00f3ff',
        };
      }
      return {
        type: 'marginalia',
        text: cleaned,
        speaker: 'SYSTEM LOG',
        accentColor: '#00f3ff',
      };
    }

    // 0. Detect uncut novel [SPEAKER: ... | ZONE: ... | PROSODY: ...] tags
    const taggedSpeakerMatch = cleaned.match(
      /^\[SPEAKER:\s*([A-Z0-9_]+)(?:\s*\|\s*ZONE:\s*([^\]|]+))?(?:\s*\|\s*PROSODY:\s*([^\]|]+))?\]\s*(.+)$/i
    );
    if (taggedSpeakerMatch) {
      const tagSpeaker = taggedSpeakerMatch[1].toUpperCase();
      const zone = taggedSpeakerMatch[2]?.trim();
      const prosody = taggedSpeakerMatch[3]?.trim();
      let rawSpeech = taggedSpeakerMatch[4].trim();

      // Clean up bracketed respiration tokens if any at start, or preserve for neural models
      let charId = 'marcus_ward';
      let name = 'Chief Marcus Ward';
      let role = 'Lead Diagnostics Engineer';
      let accent = '#00f3ff';

      if (tagSpeaker.includes('ELENA')) {
        charId = 'elena_cruz';
        name = 'Chief Architect Elena Cruz';
        role = 'Chief Jump Architect';
        accent = '#00e5ff';
      } else if (tagSpeaker.includes('NARRATOR')) {
        charId = 'narrator';
        name = 'The Elder Narrator';
        role = 'The Void Chronicler';
        accent = '#6b7c96';
      } else if (tagSpeaker.includes('SILAS')) {
        charId = 'cadet_silas';
        name = 'Cadet Silas Vance';
        role = 'Junior Technician';
        accent = '#ffd600';
      } else if (tagSpeaker.includes('VERA')) {
        charId = 'sister_vera';
        name = 'Sister Vera Kahl';
        role = 'Tribunal Inquisitress';
        accent = '#ff007f';
      } else if (tagSpeaker.includes('BENNY')) {
        charId = 'tick_tock_benny';
        name = 'Tick-Tock Benny';
        role = 'Hawser-Walker';
        accent = '#ffaa00';
      } else if (tagSpeaker.includes('MAMA') || tagSpeaker.includes('JO')) {
        charId = 'mama_jo';
        name = 'Mama Jo Vane';
        role = 'Kilo-Six Quartermaster';
        accent = '#ff5722';
      } else if (tagSpeaker.includes('THORNE')) {
        charId = 'inquisitor_thorne';
        name = 'Inquisitor Thorne';
        role = 'Tribunal Apostolic Inquisitor';
        accent = '#ff1744';
      } else if (tagSpeaker.includes('MARGIN') || tagSpeaker.includes('SPECTATOR')) {
        charId = 'margin_spectator';
        name = 'The Margin Spectator';
        role = 'Extra-Temporal Apex Observer';
        accent = '#d500f9';
      } else if (tagSpeaker.includes('PETER') || tagSpeaker.includes('SIMON')) {
        charId = 'dr_simon_peter';
        name = 'Dr. Simon Peter';
        role = 'Psychiatric Officer';
        accent = '#b388ff';
      }

      const emotionCue = detectEmotionFromText(rawSpeech, name, prosody || zone);

      return {
        type: 'dialogue',
        text: rawSpeech,
        speaker: name,
        speakerRole: role,
        characterId: charId,
        accentColor: accent,
        emotion: emotionCue.tag,
        emotionCategory: emotionCue.category,
        emotionColor: emotionCue.color,
        stageDirection: zone ? `${zone} // ${prosody || 'VOCAL PROSODY'}` : prosody,
      };
    }

    // 2. Detect character dialogues with speaker prefixes (supporting optional explicit [EMOTION CUE] tags)
    const speakerMatch = cleaned.match(
      /^(Marcus\s*Ward|Marcus|Elena\s*Cruz|Elena|Dr\.\s*Simon\s*Peter|Dr\.\s*Peter|Simon|Cadet\s*Silas\s*Vance|Silas|Sister\s*Vera\s*Kahl|Sister\s*Vera|Mama\s*Jo\s*Vane|Mama\s*Jo|Benny\s*Drost|Tick-Tock\s*Benny|Benny|Inquisitor\s*Thorne|Thorne|The\s*Margin\s*Spectator|Margin\s*Spectator|VOX-9|Ship\s*Computer|Autopilot)(?:\s*\[([^\]]+)\])?\s*[:—–-]\s*(.+)$/i
    );
    if (speakerMatch) {
      const name = speakerMatch[1].trim();
      const explicitEmotion = speakerMatch[2]?.trim();
      let rawSpeech = speakerMatch[3].trim().replace(/^["“](.*)["”]$/, '$1').trim();
      
      // Check if rawSpeech begins with a bracketed stage direction [whispering ...]
      let stageDir: string | undefined;
      const stageMatch = rawSpeech.match(/^\[([^\]]+)\]\s*(.*)$/);
      if (stageMatch) {
        stageDir = stageMatch[1].trim();
        rawSpeech = stageMatch[2].trim().replace(/^["“](.*)["”]$/, '$1').trim();
      }

      let charId = 'marcus_ward';
      let accent = '#00f3ff';
      let role = 'Senior Jump Engineer';

      if (/elena/i.test(name)) {
        charId = 'elena_cruz';
        accent = '#00e5ff';
        role = 'Chief Jump Architect';
      } else if (/peter|simon/i.test(name)) {
        charId = 'dr_simon_peter';
        accent = '#b388ff';
        role = 'Psychiatric Officer';
      } else if (/silas/i.test(name)) {
        charId = 'cadet_silas';
        accent = '#ffd600';
        role = 'Junior Technician';
      } else if (/vera/i.test(name)) {
        charId = 'sister_vera';
        accent = '#ff007f';
        role = 'Tribunal Inquisitress';
      } else if (/benny/i.test(name)) {
        charId = 'tick_tock_benny';
        accent = '#ffaa00';
        role = 'Hawser-Walker';
      } else if (/mama|jo/i.test(name)) {
        charId = 'mama_jo';
        accent = '#ff5722';
        role = 'Kilo-Six Quartermaster';
      } else if (/thorne/i.test(name)) {
        charId = 'inquisitor_thorne';
        accent = '#ff1744';
        role = 'Combine Inquisitor';
      } else if (/margin|spectator/i.test(name)) {
        charId = 'margin_spectator';
        accent = '#d500f9';
        role = 'Extra-Temporal Observer';
      } else if (/vox|computer|autopilot|telemetry/i.test(name)) {
        charId = 'vox9_telemetry';
        accent = '#00e676';
        role = 'Autonomous Nav-Telemetry';
      }

      const emotionCue = detectEmotionFromText(rawSpeech, name, explicitEmotion || stageDir);

      return {
        type: 'dialogue',
        text: rawSpeech,
        speaker: name,
        speakerRole: role,
        characterId: charId,
        accentColor: accent,
        emotion: emotionCue.tag,
        emotionCategory: emotionCue.category,
        emotionColor: emotionCue.color,
        stageDirection: stageDir,
      };
    }

    // 3. Detect quoted dialogue in narrative paragraphs with attributed speaker
    if (/^["“]/.test(cleaned)) {
      if (/\b(?:Elena|Cruz)\b/i.test(cleaned)) {
        return {
          type: 'dialogue',
          text: cleaned,
          speaker: 'Elena Cruz',
          speakerRole: 'Chief Jump Architect',
          characterId: 'elena_cruz',
          accentColor: '#00e5ff',
        };
      } else if (/\b(?:Peter|Simon)\b/i.test(cleaned)) {
        return {
          type: 'dialogue',
          text: cleaned,
          speaker: 'Dr. Simon Peter',
          speakerRole: 'Psychiatric Officer',
          characterId: 'dr_simon_peter',
          accentColor: '#b388ff',
        };
      } else if (/\b(?:Silas|Vance)\b/i.test(cleaned)) {
        return {
          type: 'dialogue',
          text: cleaned,
          speaker: 'Cadet Silas Vance',
          speakerRole: 'Junior Technician',
          characterId: 'cadet_silas',
          accentColor: '#ffd600',
        };
      } else if (/\b(?:Thorne|Inquisitor)\b/i.test(cleaned)) {
        return {
          type: 'dialogue',
          text: cleaned,
          speaker: 'Inquisitor Thorne',
          speakerRole: 'Combine Inquisitor',
          characterId: 'inquisitor_thorne',
          accentColor: '#ff1744',
        };
      } else if (/\b(?:Vera|Kahl)\b/i.test(cleaned)) {
        return {
          type: 'dialogue',
          text: cleaned,
          speaker: 'Sister Vera Kahl',
          speakerRole: 'Tribunal Inquisitress',
          characterId: 'sister_vera',
          accentColor: '#ff007f',
        };
      } else if (/\b(?:Mama\s*Jo|Jo\s*Vane)\b/i.test(cleaned)) {
        return {
          type: 'dialogue',
          text: cleaned,
          speaker: 'Mama Jo Vane',
          speakerRole: 'Kilo-Six Quartermaster',
          characterId: 'mama_jo',
          accentColor: '#ff5722',
        };
      } else if (/\b(?:Benny|Drost)\b/i.test(cleaned)) {
        return {
          type: 'dialogue',
          text: cleaned,
          speaker: 'Tick-Tock Benny',
          speakerRole: 'Hawser-Walker',
          characterId: 'tick_tock_benny',
          accentColor: '#ffaa00',
        };
      } else if (/\b(?:Marcus|Ward)\b/i.test(cleaned)) {
        return {
          type: 'dialogue',
          text: cleaned,
          speaker: 'Marcus Ward',
          speakerRole: 'Senior Jump Engineer',
          characterId: 'marcus_ward',
          accentColor: '#00f3ff',
        };
      }
    }

    return {
      type: 'narrative',
      text: cleaned,
    };
  });
}

/**
 * Determines whether a given paragraph or dialogue line is currently being spoken aloud.
 * Handles fuzzy quotation marks, whitespace, and leading prefixes.
 */
export function isTextBeingSpoken(speakingText: string, paragraphText: string): boolean {
  if (!speakingText || !paragraphText) return false;
  const s = speakingText.trim().toLowerCase().replace(/^[“"']+|[”"']+$/g, '');
  const p = paragraphText.trim().toLowerCase().replace(/^[“"']+|[”"']+$/g, '');
  if (s === p || p.includes(s) || s.includes(p)) return true;
  const sPrefix = s.slice(0, 35);
  return sPrefix.length > 12 && p.includes(sPrefix);
}

const PHONETIC_DIGITS: Record<string, string> = {
  '0': 'zero', '1': 'one', '2': 'two', '3': 'three', '4': 'four',
  '5': 'five', '6': 'six', '7': 'seven', '8': 'eight', '9': 'nine',
  'a': 'ay', 'b': 'bee', 'c': 'see', 'd': 'dee', 'e': 'ee', 'f': 'eff'
};

/**
 * Naturalizes technical sci-fi prose and numbers into spoken phonetics for speech synthesis:
 * - 0x1F3C -> 'hex zero-x-one-eff-three-see'
 * - '//' -> ', double forward slash, '
 * - '/' -> ' forward slash '
 * - 777 -> 'seven-seven-seven'
 * - 0.003 -> 'zero point zero-zero-three'
 * - Em-dashes '—' formatted for natural speech pauses
 */
export function naturalizeSpeechForTTS(text: string): string {
  if (!text) return '';
  let cleaned = text
    .replace(/^ACT\s+[IVXLCDM]+:[^—–-]+[—–-]\s*/i, '')
    .replace(/^\[PAGE\s+\d+\s+OF\s+\d+\]\s*[—–-]\s*/i, '')
    .replace(/\[[A-Za-z0-9_\-\s//—]+\]/g, '');

  // 1. Expand hex addresses
  cleaned = cleaned.replace(/\b0x([0-9a-fA-F]+)\b/g, (_m, raw) => {
    const spelled = raw.toLowerCase().split('').map((c: string) => PHONETIC_DIGITS[c] || c).join('-');
    return `hex zero-x-${spelled}`;
  });

  // 2. Expand double forward slash
  cleaned = cleaned.replace(/\s*\/\/\s*/g, ', double forward slash, ');

  // 3. Expand single slash between alphanumeric tokens
  cleaned = cleaned.replace(/(\b\w+)\s*\/\s*(\w+\b)/g, '$1 forward slash $2');

  // 4. Expand repeated numbers (e.g. 777, 000, 999)
  cleaned = cleaned.replace(/(\d)\1{1,}/g, (match, digit) => {
    const word = PHONETIC_DIGITS[digit] || digit;
    return Array(match.length).fill(word).join('-');
  });

  // 5. Expand decimals (e.g. 0.003)
  cleaned = cleaned.replace(/\b(\d+)\.(\d+)\b/g, (_m, whole, dec) => {
    const decSpelled = dec.split('').map((d: string) => PHONETIC_DIGITS[d] || d).join('-');
    return `${whole} point ${decSpelled}`;
  });

  // 6. Conversational dashes
  cleaned = cleaned.replace(/\s*—\s*/g, '— ');

  return cleaned.trim();
}

